import PropTypes from "prop-types";
import { useEffect } from "react";
import { bindActionCreators } from "redux";
import {
	fetchPrepackagePricesOfAlternativeCity,
	fetchPrepackagePricesOfCity,
	fetchPrepackagePricesOfDurations,
} from "./prePackageOfferActionCreators";
import { clearQuotation } from "app/pages/Booking/Quotation/quotationActionCreators";
import { acceptCookyPolicy } from "app/pages/.shared/CookiePolicyFooter/cookiePolicyActionCreators";
import {
	updateBooking,
	updateBookingChildrenBirthdate,
	updateBookingPassager,
} from "../Booking/bookingActionCreators";
import SearchEngine from "./components/SearchEngine.jsx";
import { bookingPropTypes } from "app/utils/propTypes";
import {
	getAllAvailableDurations,
	getAlternativeDepartureCities,
	getAvailablePrepackageOffers,
	getBookingOfferLabel,
	getDurations,
	getMainDepartureCities,
	getMonths,
	getQuotationPayload,
} from "app/pages/PrePackage/searchEngineSelectors.js";
import {
	FS_SEARCH_ENGINE_EVENT_NAME,
	OFFER_TYPES,
	PASSENGER_TYPE,
	PRODUCT_PAGE_CODE_STATUS,
	QUOTATION_CODE_STATUS,
} from "app/constants";
import { pushTagOnSearchEngineSubmit } from "app/utils/analytics";
import { updateBookingInfantsBirthdate } from "app/pages/Booking/bookingActionCreators";
import {
	getOutreMerDepartureCities,
	selectBookingDataToHydrate,
} from "app/pages/PrePackage/searchEngineSelectors";
import { isBookingEligibleForDeposit } from "app/pages/Booking/bookingSelectors";
import get from "lodash/get";
import {
	finishLoadingBar,
	showLoadingBar,
} from "app/pages/.shared/LoadingBar/loadingBarActionCreators";
import { hasTrainTransportation } from "app/pages/Listing/productsSelector";
import { isUsaDestination, isProductExpired } from "app/pages/FicheProduit/ficheProduitSelector";
import {
	getHomeImages,
	getPartnerAuthLayer,
	getPartnerDepositPercentage,
} from "app/reducers/partnerSelector";
import { buildQuotationQueryParams } from "app/utils/urlDataExtractor";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAvailablePaymentMethods } from "app/pages/Booking/Payment/paymentSelector";
import {
	getAllCalendarDates,
	getStressMarketingLabel,
} from "app/pages/StressMarketing/stressMarketingSelector";
import { EventCategory, HitType, useFlagship } from "@flagship.io/react-sdk";
import { isFlashsaleProduct } from "app/utils/utils";

const SearchEngineContainer = props => {
	const {
		booking,
		uriToFetchForFA,
		uriToFetchForAO,
		fetchPrepackagePricesOfAlternativeCity,
		fetchPrepackagePricesOfCity,
		fetchPrepackagePricesOfDurations,
		ficheProduitStatus,
		clearQuotation,
		adultOnly,
		updateBooking,
		quotationQueryParams,
	} = props;

	const navigate = useNavigate();
	const { sendHits } = useFlagship();

	const syncPrePackageOfferPrices = () => {
		const departureCityCode = booking?.departureCity?.code;
		const isAlternativeDepartureCity = booking?.departureCity?.isAlternative;
		const durationValue = booking?.duration?.value;
		const offerCode = booking?.offer?.type;
		const contractType = booking?.offer?.contractType;

		if (
			uriToFetchForFA &&
			departureCityCode &&
			offerCode === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
		) {
			if (isAlternativeDepartureCity) {
				fetchPrepackagePricesOfAlternativeCity(
					uriToFetchForFA,
					departureCityCode,
					offerCode,
					contractType
				);
			} else {
				fetchPrepackagePricesOfCity(
					uriToFetchForFA,
					departureCityCode,
					offerCode,
					contractType
				);
			}
		}

		if (durationValue && offerCode === OFFER_TYPES.ACCOMODATION_ONLY) {
			fetchPrepackagePricesOfDurations(
				uriToFetchForAO,
				durationValue,
				offerCode,
				contractType
			);
		}
	};

	const handleSubmit = (values, actions) => {
		pushTagOnSearchEngineSubmit();

		sendHits({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FS_SEARCH_ENGINE_EVENT_NAME.CLICK_ON_PP_SEARCH_CTA,
		});

		actions.setSubmitting(false);

		showLoadingBar();

		clearQuotation();

		// Passenger count and birthdates are persisted to avoid the user to enter them again.
		// But if the product is adultOnly, we remove children and babies count and birthdate
		// from the store. Children and babies data are not persisted anymore for the next product.
		if (adultOnly) {
			const passengersAdultOnly = booking.passengers.filter(passenger => {
				return passenger.type === PASSENGER_TYPE.ADULT;
			});

			updateBooking({
				infants: 0,
				children: 0,
				passengers: passengersAdultOnly,
			});
		}

		navigate({
			pathname: "/booking/quote",
			search: `?${quotationQueryParams}`,
		});
	};

	// Au changement d'offre, on tente de mettre à jour le calendrier de prix si possible
	useEffect(() => {
		if (ficheProduitStatus === PRODUCT_PAGE_CODE_STATUS.SUCCESS) {
			syncPrePackageOfferPrices();
		}

		return () => {
			finishLoadingBar();
			acceptCookyPolicy();
		};
	}, [booking.offer?.type, ficheProduitStatus]);

	return (
		<SearchEngine
			syncPrePackageOfferPrices={syncPrePackageOfferPrices}
			{...props}
			handleSubmit={handleSubmit}
		/>
	);
};

SearchEngineContainer.propTypes = {
	uriToFetchForFA: PropTypes.string,
	uriToFetchForAO: PropTypes.string,
	route: PropTypes.object,
	fetchPrepackagePricesOfCity: PropTypes.func,
	fetchPrepackagePricesOfDurations: PropTypes.func,
	prePackageOffer: PropTypes.object,
	productUri: PropTypes.string,
	productId: PropTypes.string,
	productInfo: PropTypes.object,
	mealBasis: PropTypes.string,
	booking: bookingPropTypes,
	updateBooking: PropTypes.func,
	acceptCookyPolicy: PropTypes.func,
	formInitialValues: PropTypes.object,
	isDealExpired: PropTypes.bool,
	fetchPrepackagePricesOfAlternativeCity: PropTypes.func,
	ficheProduitStatus: PropTypes.string,
	clearQuotation: PropTypes.func,
	adultOnly: PropTypes.bool,
	quotationQueryParams: PropTypes.string,
};

const mapStateToProps = state => {
	const quotationPayload = getQuotationPayload(state.ficheProduit.uri)(state);
	const currentBooking = state.booking;

	const selectedQuotationPayloadData = selectBookingDataToHydrate(state.ficheProduit.uri)(state);

	const lastBooking = {
		...currentBooking,
		...selectedQuotationPayloadData,
	};
	const departureCities = getMainDepartureCities(state);
	const outreMerDepartureCities = getOutreMerDepartureCities(state);
	const alternativeDepartureCities = getAlternativeDepartureCities(state);

	// TODO Refacto selector avec arguments
	const durations = getDurations(state.booking.offer.type || get(quotationPayload, "offer.type"))(
		state
	);

	// TODO Refacto selector avec arguments
	const pricesPerMonth = getAllCalendarDates(
		state.booking.offer.type,
		state.booking.offer.contractType
	)(state);

	// TODO Refacto selector avec arguments
	const months = getMonths(state.booking.offer.type, state.booking.offer.contractType)(state);

	const prePackageOffers = getAvailablePrepackageOffers(state);

	const prepackageOfferForAO = prePackageOffers.find(
		offer => get(offer, "pricing.type") === OFFER_TYPES.ACCOMODATION_ONLY
	);

	const prepackageOfferForFA = prePackageOffers.find(
		offer => get(offer, "pricing.type") === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION
	);
	const departureCityCode = get(lastBooking, "departureCity.code");
	const isAlternativeDepartureCity = get(lastBooking, "departureCity.isAlternative");

	let departuryCityObject = {};

	if (isAlternativeDepartureCity) {
		departuryCityObject =
			departureCityCode &&
			prepackageOfferForFA &&
			prepackageOfferForFA.pricing.alternativeDepartureCities.find(
				city => city.code === departureCityCode
			);
	} else {
		departuryCityObject =
			departureCityCode &&
			prepackageOfferForFA &&
			prepackageOfferForFA.pricing.departureCities.find(
				city => city.code === departureCityCode
			);
	}

	const formInitialValues = {
		departureCity: departureCityCode,
		departureDate: String(lastBooking?.departureDate),
		offerType: lastBooking?.offer?.type,
		duration: String(lastBooking?.duration?.value),
		adults: get(lastBooking, "adults"),
		children: get(lastBooking, "children"),
		infants: get(lastBooking, "infants"),
	};

	const isChildrenAgeRequired = state.ficheProduit.isChildAgeRequired;

	if (isChildrenAgeRequired) {
		// we have to display one date input filed per child and infant

		const childrenBirthdates = lastBooking.passengers
			.filter(passenger => passenger.type === PASSENGER_TYPE.CHILD)
			.map(
				passenger =>
					passenger.birthdate || { day: undefined, month: undefined, year: undefined }
			);
		const infantsBirthdates = lastBooking.passengers
			.filter(passenger => passenger.type === PASSENGER_TYPE.INFANT)
			.map(
				passenger =>
					passenger.birthdate || { day: undefined, month: undefined, year: undefined }
			);

		if (childrenBirthdates.length) {
			formInitialValues.childrenBirthdates = childrenBirthdates;
		}

		if (infantsBirthdates.length) {
			formInitialValues.infantsBirthdates = infantsBirthdates;
		}
	}
	const booking = state.booking;
	const stressMarketingMsg = getStressMarketingLabel(booking?.departureDate)(state);

	const authLayerTexts = getPartnerAuthLayer(state);

	const isFlashsale = isFlashsaleProduct(state.ficheProduit);

	return {
		protectedPageTexts: authLayerTexts.protectedPage,
		productId: state.ficheProduit.id,
		allAvailableDurations: getAllAvailableDurations(state),
		prePackageOffers,
		prePackageOffer: state.prePackageOffer,
		selectedOfferLabel: getBookingOfferLabel(state),
		uriToFetchForAO: get(prepackageOfferForAO, "pricing.uri"),
		uriToFetchForFA: departuryCityObject ? departuryCityObject.uri : "",
		strategy: state.partner.strategy,
		hasQuoteFullPropertyWarning: state.quotation.status === QUOTATION_CODE_STATUS.FULL_PROPERTY,
		ficheProduitStatus: state.ficheProduit.status,
		isFlashsale,
		productUri: state.ficheProduit.uri,
		mealBasis: state.ficheProduit.fromPriceType?.mealBasis,
		adultOnly: state.ficheProduit.adultOnly,
		isChildrenAgeRequired: isChildrenAgeRequired,
		productInfo: state.ficheProduit.i,
		booking: booking,
		isBookingEligibleForDeposit: isBookingEligibleForDeposit(state),
		prices: pricesPerMonth,
		resolution: state.resolution,
		departureCities,
		outreMerDepartureCities,
		alternativeDepartureCities,
		durations,
		months,
		formInitialValues,
		uuid: state.auth.uuid,
		brand: state.brand.code,
		travelByTrainAvailable: hasTrainTransportation(state),
		shop: state.shop,
		isDealExpired: isProductExpired(state),
		home: getHomeImages(state),
		showTransportOfferOnly: state.partner.showTransportOfferOnly,
		depositPercentage: getPartnerDepositPercentage(state),
		quotationQueryParams: buildQuotationQueryParams(state),
		stressMarketingMsg: stressMarketingMsg,
		availablePaymentMethods: getAvailablePaymentMethods(state),
		warningMessage: isUsaDestination(state),
		productPriceValue: state.ficheProduit.fromPriceType?.value,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBooking,
			updateBookingPassager,
			updateBookingChildrenBirthdate: updateBookingChildrenBirthdate,
			updateBookingInfantsBirthdate: updateBookingInfantsBirthdate,
			fetchPrepackagePricesOfCity,
			fetchPrepackagePricesOfAlternativeCity,
			fetchPrepackagePricesOfDurations,
			acceptCookyPolicy,
			showLoadingBar,
			finishLoadingBar,
			clearQuotation,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(SearchEngineContainer);
